function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// 0이거나 값이 없으면 빈칸, 아니면 정수/소수 한 자리로 표시
function cell(v) {
  if (v === null || v === undefined || Number(v) === 0) return '';
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function pct(done, legal) {
  if (!legal || legal <= 0) return '';
  const p = Math.min(100, Math.round((done / legal) * 100));
  return p === 0 ? '' : `${p}%`;
}

// 학생 한 명의 시수현황을 A4 인쇄용 화면으로 연다. 열린 창에서 [PDF 저장/인쇄]를 누르면
// 브라우저 인쇄 창이 뜨고, 대상(프린터)을 "PDF로 저장"으로 고르면 PDF 파일로 저장된다.
// info: { year, grade, classLabel, student: { studid, name } }
// groups: [{ subject, rows: [{ chapter, legalThry, legalPrac, thry, prac }], legalThry, legalPrac, thry, prac }]
// totals: { legalThry, legalPrac, thry, prac }
// exam: (선택) 교육결과종합용 평가결과 [{ subject, isFinal, rows: [{ no, tDate, tScore, pDate, pScore }] }] - 주면 시수표 아래에 평가결과 표를 붙인다
// opts.heading: 본문 맨 위 제목(생략하면 기본 제목)
// 인쇄 문서의 스타일(css)과 본문(body) 조각을 만든다. 다른 인쇄물(이수증명 등)에 이어 붙일 때도 쓴다.
export function buildStudentHoursParts(info, groups, totals, exam, opts = {}) {
  const examHtml = exam
    ? `
  <h2 class="section">평가결과</h2>
  <table class="exam">
    <colgroup>
      <col style="width:24%" /><col style="width:10%" /><col style="width:20%" /><col style="width:13%" /><col style="width:20%" /><col style="width:13%" />
    </colgroup>
    <thead>
      <tr><th rowspan="2">과목</th><th rowspan="2">회수</th><th colspan="2">학과</th><th colspan="2">실기</th></tr>
      <tr><th>일자</th><th>점수</th><th>일자</th><th>점수</th></tr>
    </thead>
    <tbody>
      ${exam
        .map((g) =>
          g.rows
            .map(
              (r, i) => `
      <tr>
        ${i === 0 ? `<td class="subject" rowspan="${g.rows.length}"${g.isFinal ? ' colspan="2"' : ''}>${escapeHtml(g.subject)}</td>` : ''}
        ${g.isFinal ? '' : `<td>${r.no}</td>`}
        <td>${escapeHtml(r.tDate)}</td><td>${escapeHtml(r.tScore)}</td>
        <td>${escapeHtml(r.pDate)}</td><td>${escapeHtml(r.pScore)}</td>
      </tr>`
            )
            .join('')
        )
        .join('')}
    </tbody>
  </table>`
    : '';

  const bodyRows = groups
    .map((g) => {
      const chapterRows = g.rows
        .map(
          (r, i) => `
      <tr>
        ${i === 0 ? `<td class="subject" rowspan="${g.rows.length + 1}">${escapeHtml(g.subject)}</td>` : ''}
        <td class="chapter">${escapeHtml(r.chapter)}</td>
        <td>${cell(r.legalThry)}</td>
        <td>${cell(r.legalPrac)}</td>
        <td>${cell(r.thry)}</td>
        <td>${cell(r.prac)}</td>
        <td>${pct(r.thry + r.prac, r.legalThry + r.legalPrac)}</td>
      </tr>`
        )
        .join('');
      const subtotal = `
      <tr class="subtotal">
        <td class="chapter">소계</td>
        <td>${cell(g.legalThry)}</td>
        <td>${cell(g.legalPrac)}</td>
        <td>${cell(g.thry)}</td>
        <td>${cell(g.prac)}</td>
        <td>${pct(g.thry + g.prac, g.legalThry + g.legalPrac)}</td>
      </tr>`;
      return chapterRows + subtotal;
    })
    .join('');

  const title = `${info.year}학년도 ${escapeHtml(info.student.name)} ${exam ? '교육결과종합' : '시수현황'}`;
  const css = `
  * { box-sizing: border-box; }
  @page { size: A4 portrait; margin: 15mm 12mm; }
  body {
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
    color: #111;
    margin: 0;
    padding: 24px;
  }
  h1 { text-align: center; font-size: 22px; margin: 0 0 14px; }
  .info {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    justify-content: center;
    margin-bottom: 14px;
    font-size: 14px;
  }
  /* 테두리가 겹쳐지는(collapse) 표는 바깥 테두리 절반이 표 폭 밖으로 나와 인쇄 영역 끝에서 오른쪽 선이 잘리므로 양옆을 1px씩 비운다 */
  table { width: calc(100% - 2px); margin: 0 1px; border-collapse: collapse; table-layout: fixed; }
  th, td {
    border: 1px solid #000;
    padding: 4px 4px;
    font-size: 12px;
    text-align: center;
    vertical-align: middle;
  }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  thead th { background: #f2f2f2; font-weight: 700; }
  td.subject { font-weight: 700; background: #fafafa; }
  td.chapter { text-align: left; padding-left: 6px; }
  tr.subtotal td { background: #eef2ff; font-weight: 700; }
  tr.total td { background: #e5e7eb; font-weight: 700; }
  col.c-subject { width: 14%; }
  col.c-chapter { width: 31%; }
  col.c-num { width: 11%; }
  col.c-pct { width: 11%; }
  h2.section { font-size: 16px; margin: 18px 0 8px; page-break-after: avoid; }
  table.exam { page-break-inside: avoid; }
  table.exam td { height: 24px; }
  .note { margin-top: 10px; font-size: 11px; color: #555; }
  .print-bar { text-align: right; margin-bottom: 12px; }
  .print-bar button {
    padding: 6px 16px;
    font-size: 14px;
    cursor: pointer;
  }
  @media print {
    body { padding: 0; }
    .print-bar { display: none; }
  }
`;
  const body = `
  <h1>${opts.heading || (exam ? '교육결과 종합' : '학생 시수현황')}</h1>
  <div class="info">
    <span>연도 : ${info.year}</span>
    <span>학년 : ${info.grade}학년</span>
    <span>구분/반 : ${escapeHtml(info.classLabel)}</span>
    <span>학번 : ${escapeHtml(info.student.studid)}</span>
    <span>이름 : ${escapeHtml(info.student.name)}</span>
  </div>
  <table>
    <colgroup>
      <col class="c-subject" /><col class="c-chapter" />
      <col class="c-num" /><col class="c-num" /><col class="c-num" /><col class="c-num" />
      <col class="c-pct" />
    </colgroup>
    <thead>
      <tr>
        <th rowspan="2">과목</th>
        <th rowspan="2">교육내용</th>
        <th colspan="2">법정시수</th>
        <th colspan="2">이수시수</th>
        <th rowspan="2">진도율</th>
      </tr>
      <tr>
        <th>학과</th><th>실습</th><th>학과</th><th>실습</th>
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
      <tr class="total">
        <td colspan="2">시수계</td>
        <td>${cell(totals.legalThry)}</td>
        <td>${cell(totals.legalPrac)}</td>
        <td>${cell(totals.thry)}</td>
        <td>${cell(totals.prac)}</td>
        <td>${pct(totals.thry + totals.prac, totals.legalThry + totals.legalPrac)}</td>
      </tr>
    </tbody>
  </table>${examHtml}
  <div class="note">※ 0인 칸은 빈칸으로 표시됩니다. 이수시수는 출석으로 인정된 시수입니다.</div>
`;
  return { title, css, body };
}

export function openStudentHoursPrint(info, groups, totals, exam) {
  const { title, css, body } = buildStudentHoursParts(info, groups, totals, exam);
  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8" />
<title>${title}</title>
<style>${css}</style>
</head>
<body>
  <div class="print-bar">
    <button onclick="window.print()">PDF 저장 / 인쇄</button>
  </div>${body}
</body>
</html>`;

  const popup = window.open('', '_blank', 'width=900,height=1000');
  if (!popup) {
    alert('팝업이 차단되었습니다. 브라우저의 팝업 차단을 해제해 주세요.');
    return;
  }
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
}
